import slidingPuzzleQuestIcon from "../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import wordWheelQuestIcon from "../assets/app-icons/word-wheel-quest-app-icon.png";
import { mailingVerifyCode } from "../services/CryptoUtil";

/**
 * Public site URLs per Puzzle Interact product (web + iOS apps).
 * App Store support URLs should use `/support/{appId}` on www.puzzleinteract.com.
 */

export const APP_SITE_ID = {
    WEB: "web",
    JIGSAW_MUSE: "jigsaw_muse",
    SLIDING_PUZZLE_QUEST: "sliding_puzzle_quest",
    WORD_WHEEL_QUEST: "word_wheel_quest",
    WORD_SEARCH_MINI: "word_search_mini",
};

/** @typedef {{ id: string, displayName: string, productLabel: string, kind: 'web' | 'mobile' }} AppSiteEntry */

/** @type {Record<string, AppSiteEntry>} */
export const APP_SITE_REGISTRY = {
    [APP_SITE_ID.WEB]: {
        id: APP_SITE_ID.WEB,
        displayName: "Puzzle Interact",
        productLabel: "Website",
        kind: "web",
    },
    [APP_SITE_ID.JIGSAW_MUSE]: {
        id: APP_SITE_ID.JIGSAW_MUSE,
        displayName: "Jigsaw Muse",
        productLabel: "Jigsaw Muse app",
        kind: "mobile",
    },
    [APP_SITE_ID.SLIDING_PUZZLE_QUEST]: {
        id: APP_SITE_ID.SLIDING_PUZZLE_QUEST,
        displayName: "Sliding Puzzle Quest",
        productLabel: "Sliding Puzzle Quest app",
        kind: "mobile",
    },
    [APP_SITE_ID.WORD_WHEEL_QUEST]: {
        id: APP_SITE_ID.WORD_WHEEL_QUEST,
        displayName: "Word Wheel Quest",
        productLabel: "Word Wheel Quest app",
        kind: "mobile",
    },
    [APP_SITE_ID.WORD_SEARCH_MINI]: {
        id: APP_SITE_ID.WORD_SEARCH_MINI,
        displayName: "Word Search Mini",
        productLabel: "Word Search Mini app",
        kind: "mobile",
    },
};

export const APP_SITE_IDS = Object.values(APP_SITE_ID);

/** Mobile apps only — website (Puzzle Interact) is not an app-feedback target. */
export const CONTACT_APP_IDS = APP_SITE_IDS.filter(
    (id) => APP_SITE_REGISTRY[id]?.kind === "mobile" && id !== APP_SITE_ID.WORD_SEARCH_MINI,
);

const PUBLIC_SITE_ORIGIN = (
    process.env.REACT_APP_PUBLIC_URL
    || "https://www.puzzleinteract.com"
).replace(/\/$/, "");

const PUBLIC_PATH = process.env.PUBLIC_URL || "";

export const COMPANY_LOGO_SRC = `${PUBLIC_PATH}/logo80.png`;
export const COMPANY_LOGO_PUBLIC_PATH = "/logo192.png";
export const COMPANY_LOGO_EMAIL_URL =
    "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/logos/logo32.png";
export const JIGSAW_MUSE_EMAIL_ICON_URL =
    "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/logos/apps/jigsaw-muse-app-icon-64x64.png";
export const WORD_WHEEL_QUEST_EMAIL_ICON_SRC = `${PUBLIC_PATH}/ad/word-wheel-quest-app-icon-64.png`;
export const SLIDING_PUZZLE_QUEST_EMAIL_ICON_SRC = `${PUBLIC_PATH}/ad/sliding-puzzle-quest-app-icon-64.png`;

function canonicalPublicOrigin() {
    return /^https?:\/\//i.test(PUBLIC_SITE_ORIGIN)
        ? PUBLIC_SITE_ORIGIN
        : `https://${PUBLIC_SITE_ORIGIN}`;
}

export function resolveCanonicalPublicAssetUrl(path = "/") {
    const p = path.startsWith("/") ? path : `/${path}`;
    return `${canonicalPublicOrigin()}${p}`;
}

export function resolveAppIconSrc(appId) {
    switch (appId) {
        case APP_SITE_ID.JIGSAW_MUSE:
            return `${PUBLIC_PATH}/ad/jigsaw-muse-app-icon-512.png`;
        case APP_SITE_ID.SLIDING_PUZZLE_QUEST:
            return slidingPuzzleQuestIcon;
        case APP_SITE_ID.WORD_WHEEL_QUEST:
            return wordWheelQuestIcon;
        default:
            return "";
    }
}

export function resolveAppIconEmailUrl(appId) {
    if (appId === APP_SITE_ID.JIGSAW_MUSE) {
        return JIGSAW_MUSE_EMAIL_ICON_URL;
    }
    const path = resolveAppIconPublicPath(appId);
    return path ? resolveCanonicalPublicAssetUrl(path) : "";
}

export function resolveAppIconPublicPath(appId) {
    switch (appId) {
        case APP_SITE_ID.JIGSAW_MUSE:
            return "/ad/jigsaw-muse-app-icon-512.png";
        case APP_SITE_ID.WORD_WHEEL_QUEST:
            return "/ad/word-wheel-quest-app-icon-512.png";
        case APP_SITE_ID.SLIDING_PUZZLE_QUEST:
            return "/ad/sliding-app-icon-2-ref.png";
        default:
            return "";
    }
}

export const CONTACT_TYPE = {
    GENERAL: "general",
    FEEDBACK: "feedback",
};

export function parseContactTypeParam(value) {
    const v = (value || "").trim().toLowerCase();
    return v === CONTACT_TYPE.FEEDBACK ? CONTACT_TYPE.FEEDBACK : CONTACT_TYPE.GENERAL;
}

export function parseContactAppParam(value) {
    const entry = resolveAppSiteEntry(value);
    if (!entry || entry.kind !== "mobile") return "";
    return entry.id;
}

/**
 * Contact form path, e.g. `/contact?type=feedback&app=jigsaw_muse`.
 * A known `app` implies feedback type.
 */
export function resolveContactPath({ type, app } = {}) {
    const params = new URLSearchParams();
    const appId = parseContactAppParam(app);
    const contactType = appId ? CONTACT_TYPE.FEEDBACK : parseContactTypeParam(type);
    if (contactType === CONTACT_TYPE.FEEDBACK) {
        params.set("type", CONTACT_TYPE.FEEDBACK);
    }
    if (appId) {
        params.set("app", appId);
    }
    const qs = params.toString();
    return qs ? `/contact?${qs}` : "/contact";
}

/** Canonical public contact URL for emails (always www / REACT_APP_PUBLIC_URL, not localhost). */
export function resolveCanonicalPublicContactUrl({ type, app } = {}) {
    return `${canonicalPublicOrigin()}${resolveContactPath({ type, app })}`;
}

/** Canonical mailing preference URL for emails, e.g. `/email/unsubscribe?email=…&verifyCode=…`. */
export function resolveCanonicalPublicMailingUrl(path, email) {
    const p = path.startsWith("/") ? path : `/${path}`;
    const base = `${canonicalPublicOrigin()}${p}`;
    const trimmed = (email || "").trim();
    if (!trimmed) return base;
    const params = new URLSearchParams();
    params.set("email", trimmed);
    if (p === "/email/unsubscribe" || p.endsWith("/unsubscribe")) {
        const verifyCode = mailingVerifyCode(trimmed);
        if (verifyCode) {
            params.set("verifyCode", verifyCode);
        }
    }
    return `${base}?${params.toString()}`;
}

export function resolveAppSiteEntry(appId) {
    if (!appId || typeof appId !== "string") return null;
    return APP_SITE_REGISTRY[appId.trim()] ?? null;
}

/** Match `/support|marketing|legal|privacy_policy|terms_of_service` with optional `/{appId}`. */
export function matchAppScopedPath(pathname) {
    if (!pathname || typeof pathname !== "string") return null;
    const m = pathname.match(/^\/(support|marketing|legal|privacy_policy|terms_of_service)(?:\/([^/]+))?\/?$/);
    if (!m) return null;
    return { prefix: m[1], appId: m[2] ?? null };
}

export function resolveAppMarketingPath(appId = APP_SITE_ID.WEB) {
    const entry = resolveAppSiteEntry(appId);
    const id = entry?.id ?? APP_SITE_ID.WEB;
    return `/marketing/${id}`;
}

export function resolveAppLegalPath(appId = APP_SITE_ID.WEB, hash = "privacy") {
    const entry = resolveAppSiteEntry(appId);
    const id = entry?.id ?? APP_SITE_ID.WEB;
    const fragment = hash.startsWith("#") ? hash : `#${hash}`;
    return `/legal/${id}${fragment}`;
}

export function isTermsOfServicePath(pathname) {
    return pathname === "/terms" || pathname === "/terms_of_service" || /^\/terms_of_service\/[^/]+$/.test(pathname || "");
}

export function isPrivacyPolicyPath(pathname) {
    return pathname === "/privacy" || pathname === "/privacy_policy" || /^\/privacy_policy\/[^/]+$/.test(pathname || "");
}

export function isLegalBasePath(pathname) {
    return pathname === "/legal" || /^\/legal\/[^/]+$/.test(pathname || "");
}

export function isAppScopedLegalPath(pathname) {
    return isLegalBasePath(pathname) || isPrivacyPolicyPath(pathname) || isTermsOfServicePath(pathname);
}

export function resolveAppSupportPath(appId = APP_SITE_ID.WEB) {
    const entry = resolveAppSiteEntry(appId);
    const id = entry?.id ?? APP_SITE_ID.WEB;
    return `/support/${id}`;
}

function isLocalDevOrigin(origin) {
    try {
        const host = new URL(origin).hostname.toLowerCase();
        return host === "localhost"
            || host === "127.0.0.1"
            || host === "[::1]"
            || host === "::1"
            || host.endsWith(".local");
    } catch {
        return false;
    }
}

/** Canonical public site origin for App Store / store-listing URLs. */
export function resolvePublicSiteOrigin() {
    if (typeof window !== "undefined") {
        const origin = window.location.origin;
        if (
            /^https?:\/\//i.test(origin)
            && !origin.startsWith("capacitor://")
            && !isLocalDevOrigin(origin)
        ) {
            return origin.replace(/\/$/, "");
        }
    }
    return PUBLIC_SITE_ORIGIN;
}

export function resolvePublicAppSupportUrl(appId = APP_SITE_ID.WEB, { platformApp = false } = {}) {
    const path = resolveAppSupportPath(appId);
    const base = resolvePublicSiteOrigin();
    const url = `${base}${path}`;
    return platformApp ? `${url}?platform=app` : url;
}

export function resolvePublicLegalUrl(appId = APP_SITE_ID.WEB, hash = "privacy", { platformApp = false } = {}) {
    const path = resolveAppLegalPath(appId, hash);
    const base = resolvePublicSiteOrigin();
    let url = `${base}${path}`;
    if (platformApp) {
        const hashIdx = url.indexOf("#");
        if (hashIdx >= 0) {
            const before = url.slice(0, hashIdx);
            const fragment = url.slice(hashIdx);
            const sep = fragment.includes("?") ? "&" : "?";
            url = `${before}${fragment}${sep}platform=app`;
        } else {
            url = `${url}?platform=app`;
        }
    }
    return url;
}

export function resolvePublicMarketingUrl(appId = APP_SITE_ID.WEB, { platformApp = false } = {}) {
    const path = resolveAppMarketingPath(appId);
    const base = resolvePublicSiteOrigin();
    const url = `${base}${path}`;
    return platformApp ? `${url}?platform=app` : url;
}

/** @typedef {'support'|'marketing'|'privacy'|'terms'} PublicLinkKind */

export const PUBLIC_LINK_KINDS = [
    { key: "support", label: "Support" },
    { key: "marketing", label: "Marketing" },
    { key: "privacy", label: "Privacy" },
    { key: "terms", label: "Terms" },
];

/**
 * Puzzle / product catalog for admin link matrix.
 * Web-only puzzle types share APP_SITE_ID.WEB URLs.
 * @type {Array<{ appId: string, label: string, note?: string, links: Record<PublicLinkKind, boolean> }>}
 */
export const PUZZLE_PUBLIC_LINK_PRODUCTS = [
    {
        appId: APP_SITE_ID.WEB,
        label: APP_SITE_REGISTRY[APP_SITE_ID.WEB].displayName,
        note: "Crossword, word search, hidden message, and other web puzzles",
        links: { support: true, marketing: true, privacy: true, terms: true },
    },
    {
        appId: APP_SITE_ID.SLIDING_PUZZLE_QUEST,
        label: APP_SITE_REGISTRY[APP_SITE_ID.SLIDING_PUZZLE_QUEST].displayName,
        note: "Mobile app — links in Settings",
        links: { support: true, marketing: true, privacy: true, terms: true },
    },
    {
        appId: APP_SITE_ID.JIGSAW_MUSE,
        label: APP_SITE_REGISTRY[APP_SITE_ID.JIGSAW_MUSE].displayName,
        note: "Mobile app",
        links: { support: true, marketing: true, privacy: true, terms: true },
    },
    {
        appId: APP_SITE_ID.WORD_WHEEL_QUEST,
        label: APP_SITE_REGISTRY[APP_SITE_ID.WORD_WHEEL_QUEST].displayName,
        note: "Mobile app — links in Settings",
        links: { support: true, marketing: true, privacy: true, terms: true },
    },
    {
        appId: APP_SITE_ID.WORD_SEARCH_MINI,
        label: APP_SITE_REGISTRY[APP_SITE_ID.WORD_SEARCH_MINI].displayName,
        note: "Mobile app",
        links: { support: true, marketing: true, privacy: true, terms: true },
    },
];

export function resolvePublicLinkUrl(appId, kind, { platformApp = false } = {}) {
    switch (kind) {
        case "support":
            return resolvePublicAppSupportUrl(appId, { platformApp });
        case "marketing":
            return resolvePublicMarketingUrl(appId, { platformApp });
        case "privacy":
            return resolvePublicLegalUrl(appId, "privacy", { platformApp });
        case "terms":
            return resolvePublicLegalUrl(appId, "terms", { platformApp });
        default:
            return null;
    }
}
